import { NavLink } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import type { SvgIconComponent } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@/components/ui/Tooltip";
import { cn } from "@/utils/cn";
import { prefetchRoute } from "@/utils/routePrefetch";
import { resolveBackendLabel, resolveNavLabel } from "@/i18n/navLabel";
import type { NavItem } from "@/components/layout/Sidebar";

export function NavLinkRow({
  item,
  collapsed,
  locked,
  inFlyout = false,
  iconOverride,
  variant = "primary",
  showIcon = true,
}: {
  item: NavItem;
  collapsed: boolean;
  locked?: boolean;
  accent?: "violet" | "slate";
  inFlyout?: boolean;
  iconOverride?: SvgIconComponent;
  /** Top-level link with icon, or indented sub-link without icon */
  variant?: "primary" | "sub";
  showIcon?: boolean;
}) {
  const { t } = useTranslation();
  const Icon = iconOverride ?? item.icon;
  const displayLabel = item.labelKey
    ? resolveBackendLabel(item.labelKey, item.label, t)
    : resolveNavLabel(item.to, item.label, t);
  const isSub = variant === "sub";
  const content = (
    <>
      {showIcon && !isSub && (
        <Icon className="shrink-0 opacity-90" sx={{ fontSize: 20 }} />
      )}
      {(!collapsed || inFlyout) && (
        <>
          <span className="flex-1 truncate">{displayLabel}</span>
          {locked && (
            <LockOutlinedIcon
              className="shrink-0 opacity-50"
              sx={{ fontSize: 14 }}
            />
          )}
        </>
      )}
    </>
  );

  const collapsedIconOnly = collapsed && !inFlyout;
  const collapsedItemClass = collapsedIconOnly
    ? "w-full justify-center !px-2"
    : "";

  if (locked) {
    const lockedNode = (
      <span
        className={cn(
          "workspace-nav-item workspace-nav-item--locked",
          isSub && "workspace-nav-item--child",
          collapsedItemClass,
          inFlyout && "workspace-nav-item--flyout",
        )}
      >
        {content}
      </span>
    );

    return (
      <Tooltip
        content={
          collapsedIconOnly
            ? t('layout.upgradeTooltip', { label: displayLabel, ns: 'common' })
            : t('layout.upgradeRequired', { ns: 'common' })
        }
        placement={collapsedIconOnly ? "right" : undefined}
      >
        {collapsedIconOnly ? (
          <span className="block w-full">{lockedNode}</span>
        ) : (
          lockedNode
        )}
      </Tooltip>
    );
  }

  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      onMouseEnter={() => prefetchRoute(item.to)}
      onFocus={() => prefetchRoute(item.to)}
      onTouchStart={() => prefetchRoute(item.to)}
      onPointerDown={() => prefetchRoute(item.to)}
      className={({ isActive }) =>
        cn(
          "workspace-nav-item",
          isSub && "workspace-nav-item--child",
          inFlyout && "workspace-nav-item--flyout",
          collapsedItemClass,
          isActive && "workspace-nav-item--active",
        )
      }
    >
      {content}
    </NavLink>
  );

  return (
    <Tooltip
      content={collapsedIconOnly ? displayLabel : undefined}
      placement={collapsedIconOnly ? "right" : undefined}
    >
      {collapsedIconOnly ? <span className="block w-full">{link}</span> : link}
    </Tooltip>
  );
}
